// Fetches the unified calendar feed for the month the user is looking at
// (±1 day buffer so timezone offsets never clip month-edge events). Refetches
// whenever the visible month or scope changes — month navigation therefore
// really changes the backend query range (the old pages fetched once, ever).
import { useCallback, useEffect, useState } from 'react';
import { getCalendarEvents } from '@/lib/hrApi';
import type { CalendarFeed, CalendarScope, WorkforceCalendarEvent, CalendarSourceStatus } from '@/lib/calendarApi';

interface CalendarFeedState {
  events: WorkforceCalendarEvent[];
  sources: CalendarSourceStatus[];
  warnings: string[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Days of look-ahead beyond the visible month.
 *
 *  The grid only draws the current month, but the side panels are all called
 *  "Upcoming" — and they filtered the SAME month-scoped feed. Viewed on 26
 *  September, a public holiday on 14 October had simply never been fetched, so
 *  the panel said "No upcoming holidays" about a holiday 18 days away that was
 *  sitting in the holiday list one screen over.
 *
 *  60 days covers the next month and a half, which is what "upcoming" means to
 *  somebody planning leave. The grid ignores the extra events — it renders by
 *  day-of-month — so this costs a slightly larger payload and nothing else. */
const LOOKAHEAD_DAYS = 60;

export function monthRange(cursor: Date): { from: string; to: string } {
  const from = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  from.setDate(from.getDate() - 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59);
  monthEnd.setDate(monthEnd.getDate() + 1);
  // Reach at least LOOKAHEAD_DAYS past TODAY, never less than the month itself —
  // so paging back to an old month still fetches that whole month and no more.
  const lookahead = new Date();
  lookahead.setDate(lookahead.getDate() + LOOKAHEAD_DAYS);
  lookahead.setHours(23, 59, 59, 0);
  const to = lookahead > monthEnd ? lookahead : monthEnd;
  return { from: from.toISOString(), to: to.toISOString() };
}

export function useCalendarFeed(scope: CalendarScope, cursor: Date): CalendarFeedState {
  const [feed, setFeed] = useState<CalendarFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { from, to } = monthRange(new Date(year, month, 1));
        const data = await getCalendarEvents({ from, to, scope });
        if (active) setFeed(data);
      } catch (e: any) {
        if (active) {
          setFeed(null);
          setError(e?.response?.data?.message || 'Could not load the calendar');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [scope, year, month, nonce]);

  return {
    events: feed?.events ?? [],
    sources: feed?.sources ?? [],
    warnings: feed?.warnings ?? [],
    loading,
    error,
    reload,
  };
}
